import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { IoShareOutline } from 'react-icons/io5';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/Discography.css';

const Discography = () => {
  const { handleTrackClick } = useContext(AudioContext);
  const [uploads, setUploads] = useState([]);
  const topTracks = uploads
    .slice()
    .sort((a, b) => (b.plays || 0) - (a.plays || 0))
    .slice(0, 6);
  const recentTracks = uploads
    .slice()
    .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
    .slice(0, 6);
  const [pinnedTracks, setPinnedTracks] = useState([]);
  const [userData, setUserData] = useState({
    artistName: '',
    profilePicture: '',
    bio: '',
  });
  const [loading, setLoading] = useState(true);
  const [likedTracks, setLikedTracks] = useState([]);
  const [plan, setPlan] = useState(null);
  const [albums, setAlbums] = useState([]);
  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const response = await axios.get(
            `https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPlan(response.data.plan);
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
      }
    };

    fetchUserPlan();
  }, [auth.currentUser]);

  useEffect(() => {
    const fetchUserDetails = async (firebaseUid) => {
      try {
        const userResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/users/firebase/${firebaseUid}`
        );
        setUserData(userResponse.data);

        const pinnedResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/pinned?firebaseUid=${firebaseUid}`
        );
        setPinnedTracks(pinnedResponse.data);

        const uploadsResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads?firebaseUid=${firebaseUid}`
        );
        setUploads(uploadsResponse.data);

        const likedResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/likes?firebaseUid=${firebaseUid}`
        );
        setLikedTracks(likedResponse.data);

        const albumResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/album?firebaseUid=${firebaseUid}`
        );
        setAlbums(albumResponse.data);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error.response ? error.response.data : error.message);
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserDetails(user.uid);
      } else {
        setLoading(false);
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const handleShareDiscography = async () => {
    const firebaseUid = auth.currentUser?.uid;
    if (!firebaseUid) {
      window.alert('You must be logged in to share your discography.');
      return;
    }

    const deepLink = `https://syncmusic.app/otheruserdiscography/${firebaseUid}`;
    const message = `Check out my discography on Sync: ${deepLink}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Share My Discography',
          text: message,
          url: deepLink,
        });
      } else {
        await navigator.clipboard.writeText(message);
        window.alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing discography:', error);
      window.alert('Failed to share discography.');
    }
  };

  const handleDeleteTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (window.confirm('Are you sure you want to delete this track?')) {
      try {
        await axios.delete('https://sync-api-troz.onrender.com/api/uploads/delete-track', {
          data: { trackId, firebaseUid },
        });
        window.alert('Track deleted successfully');
        setUploads((prevUploads) => prevUploads.filter((upload) => upload._id !== trackId));
        setPinnedTracks((prevPinned) => prevPinned.filter((pinned) => pinned._id !== trackId));
      } catch (error) {
        console.error('Error deleting track:', error);
        window.alert('Failed to delete track');
      }
    }
  };

  const handleLikeTrack = async (trackId, trackUserId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      window.alert('You must be logged in to like a track.');
      return;
    }

    if (trackUserId === currentUser.uid) {
      window.alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        { trackId, firebaseUid: currentUser.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Track liked successfully!');
      setUploads((prevUploads) =>
        prevUploads.map((upload) =>
          upload._id === trackId
            ? { ...upload, likes: (upload.likes || 0) + 1, likedBy: [...(upload.likedBy || []), currentUser.uid] }
            : upload
        )
      );
      setLikedTracks((prevLiked) =>
        prevLiked.map((track) =>
          track._id === trackId
            ? { ...track, likes: (track.likes || 0) + 1, likedBy: [...(track.likedBy || []), currentUser.uid] }
            : track
        )
      );
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      if (error.response?.data?.message === 'You have already liked this track') {
        window.alert('You have already liked this track.');
      } else {
        window.alert('An error occurred while liking the track.');
      }
    }
  };

  const handlePinTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/pin', {
        firebaseUid,
        trackId,
      });
      const pinnedResponse = await axios.get(
        `https://sync-api-troz.onrender.com/api/uploads/pinned?firebaseUid=${firebaseUid}`
      );
      setPinnedTracks(pinnedResponse.data);
    } catch (error) {
      console.error('Error pinning track:', error);
    }
  };

  const handleUnpinTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/unpin', {
        firebaseUid,
        trackId,
      });
      setPinnedTracks((prev) => prev.filter((track) => track._id !== trackId));
    } catch (error) {
      console.error('Error unpinning track:', error);
    }
  };

  const handleStarTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (!firebaseUid) {
      window.alert('You must be logged in to star a track.');
      return;
    }

    const track = likedTracks.find((t) => t._id === trackId);
    if (track?.user?.firebaseUid === firebaseUid) {
      window.alert("You can't star your own track.");
      return;
    }

    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/star', {
        trackId,
        userId: firebaseUid,
      });
      setLikedTracks((prevTracks) =>
        prevTracks.map((track) =>
          track._id === trackId
            ? {
                ...track,
                stars: (track.stars || 0) + 1,
                starredBy: [...(track.starredBy || []), firebaseUid],
              }
            : track
        )
      );
      window.alert('Track starred successfully!');
    } catch (error) {
      if (error.response?.data?.message === 'You have already starred a track from this artist') {
        window.alert('You already starred a track from this user.');
      } else {
        console.error('Error starring track:', error.response || error.message);
        window.alert('An error occurred while starring the track.');
      }
    }
  };

  if (loading) {
    return (
      <div className="discography-loading">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="discography-page">
      <div className="discography-container">
        <div className="discography-banner">
          {userData.profilePicture ? (
            <img src={userData.profilePicture} alt="Banner" className="discography-banner-image" />
          ) : (
            <div className="discography-banner-placeholder" />
          )}
          <p className="discography-artist-name">{userData.artistName}</p>
        </div>
        <div className="discography-header-container">
          <p className="discography-header">Your Library</p>
          <button onClick={handleShareDiscography} className="share-button">
            <IoShareOutline size={24} color="#ff5349" />
          </button>
        </div>
        <div className="discography-results">
          {pinnedTracks.length > 0 && (
            <div className="section">
              <div className="section-header-container">
                <p className="section-header">Pinned Tracks</p>
              </div>
              {pinnedTracks.map((track) => (
                <TrackItem
                  key={track._id}
                  track={track}
                  onPlay={() => handleTrackClick(track)}
                  onUnpin={() => handleUnpinTrack(track._id)}
                  isPinned={true}
                  onDelete={() => handleDeleteTrack(track._id)}
                  onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                  currentUserId={auth.currentUser?.uid}
                />
              ))}
            </div>
          )}
          {topTracks.length > 0 && (
            <div className="section">
              <div className="section-header-container">
                <p className="section-header">Top Tracks</p>
                <button
                  onClick={() =>
                    navigate('/my-discography/top-tracks', {
                      state: { tracks: uploads.sort((a, b) => (b.plays || 0) - (a.plays || 0)) },
                    })
                  }
                  className="see-all-text"
                >
                  See All
                </button>
              </div>
              {topTracks.map((track) => (
                <TrackItem
                  key={track._id}
                  track={track}
                  onPlay={() => handleTrackClick(track)}
                  onPin={() => handlePinTrack(track._id)}
                  onUnpin={() => handleUnpinTrack(track._id)}
                  isPinned={pinnedTracks.some((t) => t._id === track._id)}
                  onDelete={() => handleDeleteTrack(track._id)}
                  onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                  currentUserId={auth.currentUser?.uid}
                />
              ))}
            </div>
          )}
          {recentTracks.length > 0 && (
            <div className="section">
              <div className="section-header-container">
                <p className="section-header">All Tracks</p>
                <button
                  onClick={() =>
                    navigate('/my-discography/all-tracks', {
                      state: { tracks: uploads.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)) },
                    })
                  }
                  className="see-all-text"
                >
                  See All
                </button>
              </div>
              {recentTracks.map((track) => (
                <TrackItem
                  key={track._id}
                  track={track}
                  onPlay={() => handleTrackClick(track)}
                  onPin={() => handlePinTrack(track._id)}
                  onUnpin={() => handleUnpinTrack(track._id)}
                  isPinned={pinnedTracks.some((t) => t._id === track._id)}
                  onDelete={() => handleDeleteTrack(track._id)}
                  onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                  currentUserId={auth.currentUser?.uid}
                />
              ))}
            </div>
          )}
          {likedTracks.length > 0 && (
            <div className="section">
              <div className="section-header-container">
                <p className="section-header">Liked Tracks</p>
                {likedTracks.length > 6 && (
                  <button
                    onClick={() => navigate('/my-discography/liked-tracks', { state: { tracks: likedTracks } })}
                    className="see-all-text"
                  >
                    See All
                  </button>
                )}
              </div>
              {likedTracks.slice(0, 6).map((track) => (
                <TrackItem
                  key={track._id}
                  track={track}
                  onPlay={() => handleTrackClick(track)}
                  onStar={() => handleStarTrack(track._id)}
                  isPinned={pinnedTracks.some((t) => t._id === track._id)}
                  currentUserId={auth.currentUser?.uid}
                  onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                  isOwner={false}
                />
              ))}
            </div>
          )}
          {albums.length > 0 && (
            <div className="section">
              <div className="section-header-container">
                <p className="section-header">Albums</p>
              </div>
              {albums.map((album) => (
                <div
                  key={album._id}
                  onClick={() => navigate(`/album/${album._id}`)}
                  className="album-item"
                >
                  {album.coverPhoto ? (
                    <img src={album.coverPhoto} alt="Album Cover" className="album-cover" />
                  ) : (
                    <div className="album-cover-placeholder" />
                  )}
                  <p className="album-title">{album.albumTitle}</p>
                </div>
              ))}
            </div>
          )}
          {uploads.length === 0 && (
            <p className="discography-no-results">You don't have any tracks yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Discography;